<template>
  <!-- Teleport 到 body：fixed 定位不受 overflow 滚动容器裁切（PC 侧板必需） -->
  <Teleport to="body">
    <div v-if="open" class="asset-picker" :class="{ 'picker-side': isSide }" @click="close">
      <div class="dialog" :class="{ 'dialog-side': isSide }" :style="isSide ? sideStyle : undefined" @click.stop>
        <div class="header">
          <h3>{{ title || $t('seedance.assetPicker.defaultTitle') }}</h3>
          <button class="close-btn" @click="close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <!-- 来源切换：My Uploads（自己上传）/ My Generations（其它功能生成的结果）/ Demos（官方演示视频） -->
        <div class="source-tabs">
          <button
            v-for="s in SOURCE_TABS"
            :key="s.value"
            type="button"
            class="source-tab"
            :class="{ active: source === s.value }"
            @click="source = s.value"
          >{{ s.label }}<span v-if="s.isNew" class="new-badge">NEW</span></button>
        </div>

        <!-- 格式筛选 tab —— 仅全类型选择时展示；生成结果不含音频，隐藏 Audio；演示素材全是视频，隐藏筛选 -->
        <div v-if="type === 'all' && source !== 'demos'" class="type-tabs">
          <button
            v-for="tab in TYPE_TABS"
            v-show="!(source === 'generations' && tab.value === 'audio')"
            :key="tab.value"
            type="button"
            class="type-tab"
            :class="{ active: activeType === tab.value }"
            @click="activeType = tab.value"
          >{{ tab.label }}</button>
        </div>

        <div class="grid">
          <!-- 第一格：Upload media 上传框（仅 My Uploads；生成结果不可上传） -->
          <button
            v-if="source === 'uploads'"
            type="button"
            class="upload-cell"
            :disabled="uploading"
            @click="uploadInput?.click()"
          >
            <span v-if="uploading" class="up-spin" />
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
            <span class="upload-label">{{ $t('seedance.common.uploadMedia') }}</span>
          </button>
          <!-- 第二格：Import from RedGifs（仅视频语境；产出视频素材）。未登录去登录、未付费弹付费。
               Demos tab 里作为第一格展示。 -->
          <button
            v-if="(source === 'uploads' && (effectiveType === 'video' || effectiveType === 'all')) || source === 'demos'"
            type="button"
            class="upload-cell"
            @click="openRedgifsImport"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
            <span class="upload-label">{{ $t('seedance.redgifs.tile') }}</span>
          </button>
          <div
            v-for="a in assets"
            :key="a.id"
            class="asset-item"
            :class="{ selected: isSelected(a) && !directMode }"
          >
            <button
              type="button"
              class="asset-hit"
              :title="directMode ? (isAdded(a) ? $t('seedance.assetPicker.removeFromRefTip') : $t('seedance.assetPicker.addAsRefTip')) : undefined"
              @click="toggle(a)"
            >
              <img v-if="a.format === 'photo' || a.format === 'gif'" :src="a.url" :alt="a.materialId" />
              <!-- 视频：hover 自动循环播放预览 -->
              <video
                v-else-if="a.format === 'video'"
                :src="videoThumbSrc(a.url)"
                :poster="a.poster || a.previewImageUrl || undefined"
                preload="metadata"
                muted
                loop
                playsinline
                @mouseenter="($event.target as HTMLVideoElement).play().catch(() => {})"
                @mouseleave="($event.target as HTMLVideoElement).pause()"
              />
              <div v-else class="fallback" :class="{ 'fallback-audio': a.format === 'audio' }">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M9 18V5l12-2v13M6 21a3 3 0 100-6 3 3 0 000 6zm15-3a3 3 0 100-6 3 3 0 000 6z" />
                </svg>
                <span v-if="a.format === 'audio'" class="audio-file-name">{{ a.url.split('/').pop() }}</span>
              </div>
            </button>
            <div v-if="a.format === 'video'" class="video-badge">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3" /></svg>
              <span v-if="a.duration" class="video-dur">{{ a.duration }}s</span>
            </div>
            <!-- 右上角：删除（仅上传素材）+ 查看大图（hover 浮出；触屏常显） -->
            <div class="cell-actions">
              <button v-if="!a.isGeneration" type="button" class="cell-action" :aria-label="$t('seedance.assetPicker.deleteAssetAria')" @click.stop="deleteTarget = a">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 01-2 2H9a2 2 0 01-2-2L5 6"/><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                </svg>
              </button>
              <button type="button" class="cell-action" :aria-label="$t('seedance.assetPicker.previewAssetAria')" @click.stop="previewAsset = a">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
                </svg>
              </button>
            </div>
            <!-- 右下角：已添加/已选中 ✓ 圆形角标（不做边框高亮） -->
            <div v-if="isAdded(a) || isSelected(a)" class="added-badge">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
          </div>
        </div>
        <div v-if="loading && !assets.length" class="loading">{{ $t('seedance.common.loading') }}</div>
        <p v-else-if="!assets.length" class="empty-hint-line">
          {{ source === 'generations'
            ? $t('seedance.assetPicker.emptyGenerations')
            : $t('seedance.assetPicker.emptyUploads') }}
        </p>

        <input
          ref="uploadInput"
          type="file"
          :accept="ACCEPT_BY_TYPE[type]"
          :multiple="multiple"
          hidden
          @change="onUploadPicked"
        />

        <!-- 直接添加模式：无底部 Add 按钮，只留一行操作提示 -->
        <p v-if="directMode" class="direct-hint">{{ $t('seedance.assetPicker.directModeHint') }}</p>
        <div v-else-if="multiple" class="footer">
          <span class="count">{{ $t('seedance.assetPicker.selectedCount', { count: selected.length }) }}</span>
          <div class="footer-actions">
            <button class="btn ghost" @click="close">{{ $t('seedance.common.cancel') }}</button>
            <button class="btn primary" :disabled="!selected.length" @click="confirm">
              {{ selected.length ? $t('seedance.assetPicker.addButtonCount', { count: selected.length }) : $t('seedance.assetPicker.addButton') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 素材放大预览盖层（盖在 picker 之上） -->
    <div v-if="previewAsset" class="preview-overlay" @click="previewAsset = null">
      <button type="button" class="preview-close" :aria-label="$t('seedance.common.closePreview')" @click="previewAsset = null">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
      <video
        v-if="previewAsset.format === 'video'"
        :src="previewAsset.url"
        controls
        autoplay
        playsinline
        class="preview-media"
        @click.stop
      />
      <div v-else-if="previewAsset.format === 'audio'" class="preview-audio" @click.stop>
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
        </svg>
        <p class="preview-audio-name">{{ previewAsset.url.split('/').pop() }}</p>
        <audio :src="previewAsset.url" controls autoplay />
      </div>
      <img v-else :src="previewAsset.url" alt="" class="preview-media" @click.stop />
    </div>

    <!-- 删除确认弹窗 -->
    <div v-if="deleteTarget" class="confirm-overlay" @click.self="!deleting && (deleteTarget = null)">
      <div class="confirm-dialog">
        <h3>{{ $t('seedance.assetPicker.deleteTitle') }}</h3>
        <p>{{ isAdded(deleteTarget) ? $t('seedance.assetPicker.deleteBodyWithRef') : $t('seedance.assetPicker.deleteBody') }}</p>
        <div class="confirm-actions">
          <button type="button" class="btn ghost" :disabled="deleting" @click="deleteTarget = null">{{ $t('seedance.common.cancel') }}</button>
          <button type="button" class="btn danger" :disabled="deleting" @click="confirmDelete">
            {{ deleting ? $t('seedance.common.deleting') : $t('seedance.common.delete') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 超时长上传的裁剪弹窗（队列逐个处理，盖在 picker 之上） -->
    <TrimAssetDialog
      v-if="trimCurrent"
      :key="`${trimCurrent.file.name}-${trimQueue.length}`"
      :file="trimCurrent.file"
      :format="trimCurrent.format"
      :max-duration="trimCurrent.max"
      @done="onTrimmed"
      @cancel="trimQueue.shift()"
    />

    <!-- RedGifs 导入弹窗 -->
    <RedgifsImportDialog v-model="redgifsOpen" :import-fn="handleRedgifsImport" />
  </Teleport>
</template>

<script setup lang="ts">
import api from '~/api';
import RedgifsImportDialog from '~/components/seedance/RedgifsImportDialog.vue';
import TrimAssetDialog from '~/components/seedance/TrimAssetDialog.vue';
import { useAuthStore } from '~/composables/useAuthStore';
import { useCreditsDialogStore } from '~/composables/useCreditsDialog';
import { useSeedanceUpload } from '~/composables/useSeedanceUpload';
import { useSnackbarStore } from '~/composables/useSnackBarStore';
import { MAX_IMAGE_AUDIO_BYTES, MAX_VIDEO_BYTES, TYPE_DURATION_LIMIT, bytesToMB, probeBlobDuration, sumDurations } from '~/utils/seedanceMedia';
import { DEMO_REFERENCE_VIDEOS } from '~/utils/demoReferenceVideos';

const { t } = useI18n();

interface AssetItem {
  id: number | string;
  materialId: string;
  url: string;
  format?: string | null;
  poster?: string | null;
  previewImageUrl?: string | null;
  duration?: number | null;
  /** true 表示来自 My Generations（生成结果，只读，不可删/无音频） */
  isGeneration?: boolean;
}

/** 结果 URL 是否视频文件（容忍 ?query / #hash） */
function isVideoUrlExt(url?: string | null): boolean {
  if (!url) return false;
  return /\.(mp4|mov|mpeg|webm|3gp|wmv|asf|avi|mkv|m4v|ts)(\?|#|$)/i.test(url);
}

/**
 * 上传视频没有封面（poster 为空），<video preload="metadata"> 在远程 CDN 视频 / Safari 下
 * 往往不解码首帧 → 缩略图黑屏，直到 hover 播放才有画面。
 * 给 src 附加媒体片段 #t=0.1 强制浏览器 seek 到首帧并绘制为静态封面（已有 hash 则不动）。
 */
function videoThumbSrc(url?: string | null): string {
  if (!url) return '';
  return url.includes('#') ? url : `${url}#t=0.1`;
}

/**
 * 归一化素材 format 到 photo|gif|video|audio。
 * UserMaterial.format 存的是文件扩展名（jpg/png/mp4/mov...）而非归一值，
 * 不归一会全部落到音乐图标兜底分支。也兼容已归一的 photo/video/audio。
 */
function normalizeFormat(fmt?: string | null, url?: string | null): string {
  const x = String(fmt ?? '').toLowerCase();
  if (x === 'gif') return 'gif';
  if (['video', 'mp4', 'mov', 'mpeg', 'webm', '3gp', 'wmv', 'asf', 'avi', 'mkv', 'm4v', 'ts'].includes(x))
    return 'video';
  if (['audio', 'mp3', 'wav', 'm4a', 'aac', 'ogg', 'opus', 'flac', 'wma'].includes(x))
    return 'audio';
  if (['photo', 'image', 'jpg', 'jpeg', 'png', 'webp', 'bmp', 'heic', 'avif'].includes(x))
    return 'photo';
  // 未知 format —— 用 URL 扩展名兜底，仍判不出就当图片（比音乐图标合理）
  if (isVideoUrlExt(url)) return 'video';
  if (url && /\.(mp3|wav|m4a|aac|ogg|opus|flac|wma)(\?|#|$)/i.test(url)) return 'audio';
  return 'photo';
}

const props = defineProps<{
  modelValue: boolean;
  type?: 'all' | 'image' | 'video' | 'audio';
  multiple?: boolean;
  title?: string;
  /**
   * modal（默认）：全屏居中 + 暗色遮罩；
   * side：PC（≥1024px）在 anchor 面板右侧弹出（无遮罩暗化），小屏退回 modal。
   */
  variant?: 'modal' | 'side';
  /** side 模式的定位锚点（设置面板根元素），取其 getBoundingClientRect().right */
  anchor?: HTMLElement | null;
  /**
   * 页面上已加入 Reference Assets 的素材（混合数组，含 audio）。
   * 用于弹窗内上传视频/音频时的 15s 总时长预算校验。
   */
  referenceAssets?: { url: string; format?: string | null }[];
  /**
   * 直接添加模式（Reference Assets 用）：已添加素材的 url 列表。
   * 提供后：点击未添加的素材立即 emit pick、点击已添加的素材 emit remove-added，
   * 不再有勾选态和底部 Add 按钮。
   */
  addedUrls?: string[];
}>();
const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void;
  (e: 'pick', assets: AssetItem[]): void;
  (e: 'remove-added', url: string): void;
}>();

const open = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});
const type = computed(() => props.type ?? 'all');

// 素材来源：My Uploads（自己上传，可传/可删/含音频）| My Generations（其它功能生成的已完成结果，只读、无音频）
// | Demos（官方演示参考视频，静态 CDN 列表，只读）
const source = ref<'uploads' | 'generations' | 'demos'>('uploads');

const uploadAssets = ref<AssetItem[]>([]);
const genAssets = ref<AssetItem[]>([]);
const selected = ref<AssetItem[]>([]);
const loadingUploads = ref(false);
const loadingGenerations = ref(false);

// 生成结果按有效类型（image/video）过滤；生成结果不含音频
const effectiveType = computed<'all' | 'image' | 'video' | 'audio'>(() =>
  type.value === 'all' ? activeType.value : type.value,
);
const filteredGenAssets = computed(() => {
  const t = effectiveType.value;
  if (t === 'audio') return [];
  return genAssets.value.filter((g) =>
    t === 'video' ? g.format === 'video' : t === 'image' ? g.format !== 'video' : true,
  );
});
// Demos —— 官方演示参考视频（静态列表，只读、无删除；全是视频）
const demoAssets = computed<AssetItem[]>(() => {
  const t = effectiveType.value;
  if (t === 'image' || t === 'audio') return [];
  return DEMO_REFERENCE_VIDEOS.map((d) => ({
    id: d.id,
    materialId: d.id,
    url: d.url,
    format: 'video',
    poster: d.poster,
    duration: d.duration,
    isGeneration: true,
  }));
});
// 当前展示列表 / loading —— 按来源切换
const assets = computed<AssetItem[]>(() =>
  source.value === 'demos'
    ? demoAssets.value
    : source.value === 'generations'
      ? filteredGenAssets.value
      : uploadAssets.value,
);
const loading = computed(() =>
  source.value === 'demos'
    ? false
    : source.value === 'generations'
      ? loadingGenerations.value
      : loadingUploads.value,
);

// 直接添加模式（Reference Assets）：点击即添加/移除，无勾选态
const directMode = computed(() => props.addedUrls !== undefined);
const addedSet = computed(() => new Set(props.addedUrls ?? []));
const isAdded = (a: AssetItem) => addedSet.value.has(a.url);

// 素材放大预览 / 删除确认目标
const previewAsset = ref<AssetItem | null>(null);
const deleteTarget = ref<AssetItem | null>(null);
const deleting = ref(false);

const confirmDelete = async () => {
  const target = deleteTarget.value;
  if (!target) return;
  deleting.value = true;
  try {
    await api.deleteMat(target.materialId);
    // 已加入 Reference Assets 的一并移除，避免引用已删素材
    emit('remove-added', target.url);
    selected.value = selected.value.filter((s) => s.url !== target.url);
    await loadAssets();
    deleteTarget.value = null;
  } catch (err: any) {
    console.error('[AssetPickerDialog] delete failed', err);
    useSnackbarStore().showError(err?.message || t('seedance.assetPicker.toastDeleteFailed'));
  } finally {
    deleting.value = false;
  }
};

// 格式筛选 tab（仅 type="all" 时展示）；activeType 决定列表查询
const TYPE_TABS = [
  { value: 'all', label: t('seedance.assetPicker.tabAll') },
  { value: 'image', label: t('seedance.assetPicker.tabImage') },
  { value: 'video', label: t('seedance.assetPicker.tabVideo') },
  { value: 'audio', label: t('seedance.assetPicker.tabAudio') },
] as const;
// Demos 全是视频，仅视频语境展示该 tab；排第一位
const SOURCE_TABS = computed(() => [
  ...(type.value === 'all' || type.value === 'video'
    ? [{ value: 'demos' as const, label: t('seedance.assetPicker.sourceDemos'), isNew: true }]
    : []),
  { value: 'uploads' as const, label: t('seedance.assetPicker.sourceUploads'), isNew: false },
  { value: 'generations' as const, label: t('seedance.assetPicker.sourceGenerations'), isNew: false },
]);
const activeType = ref<'all' | 'image' | 'video' | 'audio'>(type.value);

const loadAssets = async () => {
  loadingUploads.value = true;
  try {
    const res = await api.seedanceListAssets({ type: activeType.value, limit: 100 });
    // format 归一化（UserMaterial 存的是扩展名 jpg/png/mp4...，不归一会全显示成音乐图标）
    uploadAssets.value = res.assets.map((a: AssetItem) => ({
      ...a,
      format: normalizeFormat(a.format, a.url),
    }));
  } catch (e) {
    console.error(e);
  } finally {
    loadingUploads.value = false;
  }
};

// My Generations —— 复用「我的创作」接口，取已完成且有结果 URL 的记录（图/视频，无音频）
const loadGenerations = async () => {
  if (genAssets.value.length || loadingGenerations.value) return;
  loadingGenerations.value = true;
  try {
    const res = await api.getMyCreations('all', 1, 60);
    // 只按「已完成 + 有结果 URL」过滤。不能再校验 order_status：
    // /my/creates 处理器已在服务端过滤 orderStatus=NORMAL，且响应里根本不返回
    // order_status 字段 → c.order_status 恒为 undefined，加这条会把所有结果误删。
    genAssets.value = (res.lists ?? [])
      .filter((c) => c.task_status === 1 && c.result_url)
      .map((c) => {
        // Seedance 结果恒为视频；其余按结果 URL 扩展名判断
        const isSeedance = (c.tool_id || '').toLowerCase().startsWith('seedance');
        const isVideo = isSeedance || isVideoUrlExt(c.result_url) || c.format === 'mp4';
        return {
          id: c.order_id,
          materialId: c.order_id,
          url: c.result_url,
          format: isVideo ? 'video' : 'photo',
          poster: null,
          isGeneration: true,
        } as AssetItem;
      });
  } catch (e) {
    console.error('[AssetPickerDialog] load generations failed', e);
  } finally {
    loadingGenerations.value = false;
  }
};

const loadForSource = () => {
  if (source.value === 'demos') return; // 静态列表，无需加载
  if (source.value === 'generations') loadGenerations();
  else loadAssets();
};

// 首次打开弹窗默认展示 Demos tab 的持久标记
const DEMOS_SEEN_KEY = 'seedance-demos-tab-seen';
watch(open, (v) => {
  if (v) {
    selected.value = [];
    // 首次打开（且 Demos 可见）默认落在 Demos tab 引导发现；之后恢复默认 My Uploads
    let firstDemo = false;
    try {
      firstDemo = (type.value === 'all' || type.value === 'video') && !localStorage.getItem(DEMOS_SEEN_KEY);
      if (firstDemo) localStorage.setItem(DEMOS_SEEN_KEY, '1');
    } catch {
      firstDemo = false;
    }
    if (firstDemo) {
      source.value = 'demos';
      activeType.value = 'all';
    } else {
      const wasUploads = source.value === 'uploads';
      source.value = 'uploads';
      activeType.value = type.value;
      // source 未变化时 watch(source) 不会触发，需手动加载
      if (wasUploads) loadAssets();
    }
    computeSidePos();
  }
});
watch(activeType, () => { if (open.value && source.value === 'uploads') loadAssets(); });
watch(source, (s) => {
  if (!open.value) return;
  // 生成结果无音频 / 演示素材全是视频 —— 切过去时把不适用的筛选重置为 All
  if (s === 'generations' && activeType.value === 'audio') activeType.value = 'all';
  if (s === 'demos' && activeType.value !== 'all') activeType.value = 'all';
  loadForSource();
});

// -------- 弹窗内上传（第一格 Upload media）——上传后刷新列表并自动选中 --------
const ACCEPT_BY_TYPE: Record<'all' | 'image' | 'video' | 'audio', string> = {
  all: 'image/*,video/*,audio/*',
  image: 'image/*',
  video: 'video/*',
  audio: 'audio/*',
};
const uploadInput = ref<HTMLInputElement>();
const { upload, uploading } = useSeedanceUpload();

// -------- 视频/音频 15s 时长预算（Kinovi：各自总和 ≤15s） --------
// 剩余预算 = 15s − 页面已加入的该类素材总时长 − 弹窗内已勾选的该类素材总时长
const remainingBudget = async (fmt: 'video' | 'audio'): Promise<number> => {
  const addedUrls = (props.referenceAssets ?? [])
    .filter((a) => (a.format ?? '') === fmt)
    .map((a) => a.url);
  const selectedUrls = selected.value.filter((s) => s.format === fmt).map((s) => s.url);
  const used = await sumDurations([...new Set([...addedUrls, ...selectedUrls])], fmt);
  return TYPE_DURATION_LIMIT - used;
};

// 超时长上传的裁剪队列（逐个弹 TrimAssetDialog）
interface TrimTask { file: File; format: 'video' | 'audio'; max: number }
const trimQueue = ref<TrimTask[]>([]);
const trimCurrent = computed(() => trimQueue.value[0] ?? null);

// 上传完成后的收尾：回基准 tab 刷新列表，直接添加 / 自动选中 / 回传
const acceptUploaded = async (urls: string[]) => {
  if (!urls.length) return;
  activeType.value = type.value;
  await loadAssets();
  const uploadedItems = assets.value.filter((a) => urls.includes(a.url));
  if (directMode.value) {
    // 直接添加模式：上传完成即添加（校验在父级 pick 处理里）
    if (uploadedItems.length) emit('pick', uploadedItems);
  } else if (props.multiple) {
    for (const it of uploadedItems) {
      if (!selected.value.some((s) => s.id === it.id)) selected.value.push(it);
    }
  } else if (uploadedItems[0]) {
    emit('pick', [uploadedItems[0]]);
    close();
  }
};

const processFiles = async (files: File[]) => {
  if (!files.length) return;
  try {
    const urls: string[] = [];
    // 同一批内多个视频/音频依次占用预算
    const batchUsed = { video: 0, audio: 0 };
    const toTrim: TrimTask[] = [];
    for (const f of files) {
      const fmt = f.type.startsWith('video/')
        ? ('video' as const)
        : f.type.startsWith('audio/')
          ? ('audio' as const)
          : null;
      if (fmt) {
        const remaining = (await remainingBudget(fmt)) - batchUsed[fmt];
        if (remaining < 1) {
          useSnackbarStore().showError(
            t(fmt === 'video' ? 'seedance.assetPicker.toastDurationBudgetVideo' : 'seedance.assetPicker.toastDurationBudgetAudio', { seconds: TYPE_DURATION_LIMIT })
          );
          continue;
        }
        const dur = await probeBlobDuration(f, fmt);
        const sizeLimit = fmt === 'video' ? MAX_VIDEO_BYTES : MAX_IMAGE_AUDIO_BYTES;
        // 超时长预算 或 超大小 → 进裁剪队列（裁短即减小时长/体积）
        if (dur > remaining + 0.3 || f.size > sizeLimit) {
          toTrim.push({ file: f, format: fmt, max: Math.max(1, Math.round(remaining * 10) / 10) });
          continue;
        }
        batchUsed[fmt] += dur;
      } else if (f.size > MAX_IMAGE_AUDIO_BYTES) {
        // 图片无裁剪，超 20MB 直接拒
        useSnackbarStore().showError(
          t('seedance.assetPicker.toastImageTooLarge', { size: bytesToMB(f.size), max: 20 })
        );
        continue;
      }
      const res = await upload(f);
      urls.push(res.url);
    }
    await acceptUploaded(urls);
    if (toTrim.length) trimQueue.value.push(...toTrim);
  } catch (err: any) {
    console.error('[AssetPickerDialog] upload failed', err);
    useSnackbarStore().showError(err?.message || t('seedance.common.uploadFailed'));
  }
};

const onUploadPicked = async (e: Event) => {
  const input = e.target as HTMLInputElement;
  const files = input.files ? Array.from(input.files) : [];
  input.value = '';
  await processFiles(files);
};

// -------- Import from RedGifs --------
const authStore = useAuthStore();
const localePath = useLocalePath();
const redgifsOpen = ref(false);

// 未登录 → 去登录；已登录未付费 → 弹付费弹窗；已付费 → 打开导入弹窗
const openRedgifsImport = async () => {
  if (!authStore.isLogin) { navigateTo(localePath('/login')); return; }
  try {
    const { hasPaid } = await api.hasPaid();
    if (!hasPaid) { useCreditsDialogStore().show(t('seedance.redgifs.errPurchase')); return; }
  } catch { /* 查询失败就放行到弹窗，由服务端 402 兜底 */ }
  redgifsOpen.value = true;
};

// 后端解析 ID + 取回 MP4 字节 → 当本地文件走现有上传/裁剪流程（≤15s 入库，>15s 进裁剪）
const handleRedgifsImport = async (input: string) => {
  const res = await fetch('/api/seedance/redgifs-import', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ input }),
  });
  if (!res.ok) {
    if (res.status === 401) { redgifsOpen.value = false; navigateTo(localePath('/login')); throw new Error(t('seedance.redgifs.errFailed')); }
    if (res.status === 402) { redgifsOpen.value = false; useCreditsDialogStore().show(t('seedance.redgifs.errPurchase')); throw new Error(t('seedance.redgifs.errPurchase')); }
    if (res.status === 400) throw new Error(t('seedance.redgifs.errInvalid'));
    if (res.status === 404) throw new Error(t('seedance.redgifs.errNotFound'));
    throw new Error(t('seedance.redgifs.errFailed'));
  }
  const blob = await res.blob();
  const file = new File([blob], `redgifs-${Date.now()}.mp4`, { type: 'video/mp4' });
  await processFiles([file]);
};

// 裁剪完成 → 校验裁剪后大小 → 上传裁剪产物并入选
const onTrimmed = async (trimmed: File) => {
  trimQueue.value.shift();
  const isVideo = trimmed.type.startsWith('video/');
  const sizeLimit = isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_AUDIO_BYTES;
  // 裁剪后仍超限（如原视频码率过高、裁到最短仍太大）→ 提示再裁短
  if (trimmed.size > sizeLimit) {
    useSnackbarStore().showError(
      t('seedance.assetPicker.toastStillTooLarge', { size: bytesToMB(trimmed.size), max: isVideo ? 50 : 20 })
    );
    return;
  }
  try {
    const res = await upload(trimmed);
    await acceptUploaded([res.url]);
  } catch (err: any) {
    console.error('[AssetPickerDialog] trimmed upload failed', err);
    useSnackbarStore().showError(err?.message || t('seedance.common.uploadFailed'));
  }
};

// -------- PC 侧板（variant="side" 且视口 ≥1024px） --------
const isDesktop = ref(false);
let mq: MediaQueryList | null = null;
const updateMq = () => { isDesktop.value = mq?.matches ?? false; };
onMounted(() => {
  mq = window.matchMedia('(min-width: 1024px)');
  updateMq();
  mq.addEventListener('change', updateMq);
  window.addEventListener('resize', computeSidePos);
});
onBeforeUnmount(() => {
  mq?.removeEventListener('change', updateMq);
  window.removeEventListener('resize', computeSidePos);
});
const isSide = computed(() => props.variant === 'side' && isDesktop.value);

// 取值依据：anchor（设置面板根元素）的 getBoundingClientRect().right + 12px 间距；
// top 取面板顶部（最少留 12px），maxHeight 用剩余视口高；resize 时重算。
// 组件整体 Teleport 到 body + fixed 定位，不受 overflow 滚动容器裁切。
const sideStyle = ref<Record<string, string>>({});
function computeSidePos() {
  if (typeof window === 'undefined') return;
  const el = props.anchor;
  if (!el) {
    sideStyle.value = { top: '80px', left: 'calc(50% - 250px)', maxHeight: 'calc(100vh - 92px)' };
    return;
  }
  const rect = el.getBoundingClientRect();
  const top = Math.max(rect.top, 12);
  const left = Math.min(rect.right + 12, window.innerWidth - 512);
  sideStyle.value = {
    top: `${top}px`,
    left: `${Math.max(left, 12)}px`,
    maxHeight: `calc(100vh - ${top + 12}px)`,
  };
}

const isSelected = (a: AssetItem) => selected.value.some((s) => s.id === a.id);
const toggle = (a: AssetItem) => {
  if (directMode.value) {
    // 已添加 → 移除；未添加 → 立即添加（数量/时长校验在父级 pick 处理里）
    if (isAdded(a)) emit('remove-added', a.url);
    else emit('pick', [a]);
    return;
  }
  if (!props.multiple) {
    emit('pick', [a]);
    close();
    return;
  }
  const idx = selected.value.findIndex((s) => s.id === a.id);
  if (idx >= 0) selected.value.splice(idx, 1);
  else selected.value.push(a);
};
const confirm = () => {
  emit('pick', selected.value);
  close();
};
const close = () => {
  selected.value = [];
  previewAsset.value = null;
  deleteTarget.value = null;
  open.value = false;
};
</script>

<style scoped>
.asset-picker {
  position: fixed;
  inset: 0;
  z-index: 90;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.dialog {
  width: 100%;
  max-width: 720px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1.25rem;
  padding: 1.25rem;
}
/* PC 侧板：无暗色遮罩（透明层只负责点外部关闭），面板 fixed 定位到锚点右侧 */
.asset-picker.picker-side {
  background: transparent;
  backdrop-filter: none;
  padding: 0;
  display: block;
}
.dialog.dialog-side {
  position: fixed;
  width: 500px;
  max-width: calc(100vw - 24px);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
}

/* 来源切换 tab（My Uploads / My Generations） */
.source-tabs {
  display: flex; gap: 0.25rem;
  margin-bottom: 0.75rem;
  padding: 0.25rem;
  border-radius: 0.6rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.source-tab {
  flex: 1;
  display: inline-flex; align-items: center; justify-content: center; gap: 0.25rem;
  padding: 0.4rem 0.5rem;
  border: none; border-radius: 0.45rem;
  background: transparent;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.75rem; font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.new-badge {
  padding: 0.05rem 0.25rem;
  border-radius: 0.25rem;
  background: var(--primary-color, #e91e63);
  color: #fff;
  font-size: 0.5rem; font-weight: 700; line-height: 1.1;
}
.source-tab:hover { color: rgba(255, 255, 255, 0.85); }
.source-tab.active {
  color: white;
  background: linear-gradient(90deg, rgba(217, 70, 239, 0.9), rgba(217, 70, 239, 0.7));
  box-shadow: 0 2px 8px rgba(217, 70, 239, 0.35);
}

/* 格式筛选 tab */
.type-tabs {
  display: flex; gap: 0.35rem;
  margin-bottom: 0.75rem;
}
.type-tab {
  padding: 0.25rem 0.75rem;
  border: none; border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.75rem;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.type-tab:hover { color: white; background: rgba(255, 255, 255, 0.12); }
.type-tab.active { color: white; font-weight: 500; background: #3f3f46; }

/* 第一格：Upload media 上传框 */
.upload-cell {
  position: relative;
  aspect-ratio: 1;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 0.35rem;
  border: 2px dashed rgba(255, 255, 255, 0.15);
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.03);
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.upload-cell:hover { color: white; background: rgba(255, 255, 255, 0.08); }
.upload-cell:disabled { cursor: not-allowed; }
.upload-label { font-size: 10px; line-height: 1.2; text-align: center; padding: 0 0.25rem; }
.up-spin {
  width: 20px; height: 20px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-top-color: #d946ef;
  animation: picker-spin 1s linear infinite;
  display: inline-block;
}
@keyframes picker-spin { to { transform: rotate(360deg); } }
.empty-hint-line {
  margin: 0.75rem 0 0;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4);
}
.fallback-audio { flex-direction: column; gap: 0.25rem; padding: 0 0.25rem; }
.audio-file-name {
  width: 100%;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  text-align: center;
  font-size: 9px; color: rgba(255, 255, 255, 0.5);
}
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}
h3 {
  color: white;
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
}
.close-btn {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
}
.close-btn:hover { color: white; }
.loading {
  padding: 1.5rem 1rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
}
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  /* auto-rows:min-content 必需：overflow-y:auto 让 .grid 成为滚动容器后，
     Chromium 会忽略 grid item 的 aspect-ratio，行高塌成图片原始高度、方格彼此重叠。
     显式 min-content 行高消除该行为（与 vidsoda 版 auto-rows-min 一致）。 */
  grid-auto-rows: min-content;
  gap: 0.5rem;
  overflow-y: auto;
  flex: 1;
}
.asset-item {
  position: relative;
  aspect-ratio: 1;
  border: 2px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.5rem;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.4);
  padding: 0;
  transition: all 0.15s;
}
.asset-item:hover { border-color: rgba(255, 255, 255, 0.2); }
.asset-item.selected { border-color: #d946ef; }
.asset-hit {
  display: block;
  width: 100%; height: 100%;
  padding: 0; border: none;
  background: transparent;
  cursor: pointer;
}
.asset-item img,
.asset-item video { width: 100%; height: 100%; object-fit: cover; display: block; }
/* 右上角 hover 操作：删除 + 查看大图（触屏常显） */
.cell-actions {
  position: absolute; top: 4px; right: 4px;
  display: flex; gap: 4px;
}
.cell-action {
  display: flex; align-items: center; justify-content: center;
  padding: 4px;
  border: none; border-radius: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.cell-action:hover { background: #000; color: #fff; }
@media (hover: hover) {
  .cell-actions { opacity: 0; transition: opacity 0.15s; }
  .asset-item:hover .cell-actions { opacity: 1; }
}
/* 右下角已添加 ✓ 圆形角标 */
.added-badge {
  pointer-events: none;
  position: absolute; bottom: 4px; right: 4px;
  width: 18px; height: 18px;
  border-radius: 50%;
  background: #d946ef;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
}
/* 直接添加模式的底部提示行 */
.direct-hint {
  margin: 0.75rem 0 0;
  padding-top: 0.6rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  text-align: center;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.45);
}
/* 素材放大预览盖层 */
.preview-overlay {
  position: fixed; inset: 0; z-index: 110;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(6px);
  display: flex; align-items: center; justify-content: center;
  padding: 1rem;
}
.preview-close {
  position: absolute; top: 1rem; right: 1rem;
  padding: 0.5rem;
  border: none; border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  display: flex;
}
.preview-close:hover { background: #000; color: #fff; }
.preview-media {
  max-width: 100%; max-height: 85vh;
  border-radius: 0.75rem;
  object-fit: contain;
}
.preview-audio {
  display: flex; flex-direction: column;
  align-items: center; gap: 1rem;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: #131313;
  color: rgba(255, 255, 255, 0.45);
}
.preview-audio-name {
  margin: 0; max-width: 280px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  font-size: 0.75rem; color: rgba(255, 255, 255, 0.5);
}
/* 删除确认弹窗 */
.confirm-overlay {
  position: fixed; inset: 0; z-index: 115;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: 1rem;
}
.confirm-dialog {
  width: 100%; max-width: 24rem;
  background: #131313;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  padding: 1.25rem;
  color: white;
}
.confirm-dialog h3 { margin: 0 0 0.5rem; font-size: 1rem; font-weight: 700; }
.confirm-dialog p { margin: 0 0 1rem; font-size: 0.8rem; color: rgba(255, 255, 255, 0.55); line-height: 1.5; }
.confirm-actions { display: flex; justify-content: flex-end; gap: 0.5rem; }
.btn.danger { background: #dc2626; color: white; }
.btn.danger:hover:not(:disabled) { background: #b91c1c; }
.fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.4);
}
.video-dur {
  margin-left: 2px;
  font-size: 9px;
  font-weight: 500;
  line-height: 1;
}
.video-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border-radius: 3px;
  padding: 2px 4px;
  display: flex;
}
.footer {
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.count { color: rgba(255, 255, 255, 0.55); font-size: 0.75rem; }
.footer-actions { display: flex; gap: 0.5rem; }
.btn {
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
}
.btn.ghost {
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.12);
}
.btn.primary {
  background: linear-gradient(90deg, #d946ef, #f43f5e);
  color: white;
}
.btn:disabled { opacity: 0.4; cursor: not-allowed; }
</style>
